import React, { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, CheckCircle, ChevronLeft } from 'lucide-react';
import ManualFmcsaForm from '@/components/onboarding/ManualFmcsaForm';

/**
 * Signup-time FMCSA verification step for brokers and carriers.
 * Runs inside the AuthPage flow AFTER phone verification and BEFORE tier
 * selection. On success, writes fmcsa_* fields + documents_verified_at to
 * profiles and hydrates the placeholder company (if provisioned) with
 * FMCSA data via the hydrate_company_from_fmcsa RPC.
 *
 * Extracted from the legacy post-login VerifyMcGate (kept intact for
 * Settings-side manual verification).
 */

interface VerifiedFmcsa {
  legalName: string;
  dbaName?: string;
  dotNumber?: string;
  mcNumber?: string;
  statusCode?: string;
  entityType?: string | null;
  phyStreet?: string | null;
  phyCity?: string | null;
  phyState?: string | null;
  phyZipcode?: string | null;
}

interface Props {
  userId: string;
  role: 'broker' | 'carrier' | 'broker_carrier';
  onVerified: () => void;
  onBack?: () => void;
}

const FmcsaProgressBar: React.FC<{ active: boolean }> = ({ active }) => {
  const [progress, setProgress] = useState(0);
  const [label, setLabel] = useState('Contacting FMCSA SAFER…');

  useEffect(() => {
    if (!active) {
      setProgress(0);
      setLabel('Contacting FMCSA SAFER…');
      return;
    }
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      const elapsedMs = Date.now() - startedAt;
      const t = Math.min(elapsedMs / 5000, 1);
      const eased = 1 - Math.pow(1 - t, 2);
      setProgress(Math.min(Math.round(eased * 98), 98));
      if (elapsedMs < 1500) setLabel('Scanning authority records…');
      else if (elapsedMs < 3500) setLabel('Cross-checking insurance & safety…');
      else setLabel('Finalizing verification…');
    }, 250);
    return () => window.clearInterval(interval);
  }, [active]);

  if (!active) return null;
  return (
    <div className="space-y-2 mt-2" aria-live="polite">
      <Progress value={progress} className="h-2" />
      <p className="text-xs text-muted-foreground text-center">{label}</p>
    </div>
  );
};

export const SignupFmcsaStep: React.FC<Props> = ({ userId, role, onVerified, onBack }) => {
  const { toast } = useToast();
  const [authorityType, setAuthorityType] = useState<'MC' | 'DOT'>(
    role === 'broker' ? 'MC' : 'DOT'
  );
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [verified, setVerified] = useState<VerifiedFmcsa | null>(null);
  const [pendingCarrier, setPendingCarrier] = useState<VerifiedFmcsa | null>(null);
  const [mode, setMode] = useState<'verify' | 'manual' | 'pending'>('verify');
  const confettiFiredRef = useRef(false);

  // Play scan animation then reveal verified card
  useEffect(() => {
    if (!scanning || !pendingCarrier) return;
    const t = window.setTimeout(() => {
      setVerified(pendingCarrier);
      setPendingCarrier(null);
      setScanning(false);
    }, 5000);
    return () => window.clearTimeout(t);
  }, [scanning, pendingCarrier]);

  useEffect(() => {
    if (verified && !confettiFiredRef.current) {
      confettiFiredRef.current = true;
      confetti({ particleCount: 120, spread: 70, origin: { y: 0.6 } });
    }
  }, [verified]);

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value.replace(/\D/g, ''));
    setError('');
    setVerified(null);
    setPendingCarrier(null);
  };

  const handleVerify = async () => {
    if (!userId || !code) return;
    setError('');
    setVerifying(true);
    try {
      const numericPart = code.trim().replace(/\D/g, '');
      if (!/^\d{5,}$/.test(numericPart)) {
        setError('Number must be at least 5 digits');
        return;
      }

      const lookupUserType = role === 'broker' ? 'broker' : 'carrier';
      // Never block signup: server flags any non-clean result for manual
      // review (Discord + pending mark). 30s client cap as a backstop.
      const TIMEOUT = Symbol('timeout');
      const result = await Promise.race([
        supabase.functions.invoke('verify-fmcsa', {
          body: { number: numericPart, authorityType, userType: lookupUserType, context: 'signup' },
          headers: { 'x-region': 'us-east-1' },
        }),
        new Promise<typeof TIMEOUT>((r) => window.setTimeout(() => r(TIMEOUT), 30000)),
      ]);

      if (result === TIMEOUT || result.error || !result.data?.valid) {
        if (result !== TIMEOUT && result.error) console.error('verify-fmcsa invocation error:', result.error);
        setMode('pending');
        return;
      }
      const data = result.data;

      const carrier: VerifiedFmcsa = {
        legalName: data.carrierInfo.legalName,
        dbaName: data.carrierInfo.dbaName,
        dotNumber: data.carrierInfo.dotNumber,
        mcNumber: data.carrierInfo.mcNumber,
        statusCode: data.carrierInfo.statusCode,
        entityType: data.carrierInfo.entityType ?? null,
        phyStreet: data.carrierInfo.phyStreet ?? null,
        phyCity: data.carrierInfo.phyCity ?? null,
        phyState: data.carrierInfo.phyState ?? null,
        phyZipcode: data.carrierInfo.phyZipcode ?? null,
      };

      const nowIso = new Date().toISOString();
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          fmcsa_code: `${authorityType}${code}`,
          fmcsa_verified: true,
          fmcsa_authority_type: authorityType,
          fmcsa_number: code.trim(),
          fmcsa_dot_number: carrier.dotNumber,
          fmcsa_mc_number: carrier.mcNumber,
          fmcsa_legal_name: carrier.legalName,
          fmcsa_dba_name: carrier.dbaName,
          fmcsa_status_code: carrier.statusCode,
          fmcsa_verified_at: nowIso,
        })
        .eq('id', userId);

      if (updateError) throw updateError;

      // Hydrate placeholder company (only exists if provisioning already ran;
      // free-tier provisioning runs later, so this is a no-op in most signups
      // and hydrate is re-run by provision-free-account triggers).
      try {
        const { error: hydrateErr } = await supabase.rpc('hydrate_company_from_fmcsa', {
          _user_id: userId,
        });
        if (hydrateErr) console.warn('[hydrate_company_from_fmcsa] non-fatal:', hydrateErr);
      } catch (hydrateEx) {
        console.warn('[hydrate_company_from_fmcsa] threw (non-fatal):', hydrateEx);
      }

      setPendingCarrier(carrier);
      setScanning(true);
      toast({ title: 'FMCSA Verified!', description: 'Your authority is on file.' });
    } catch (e) {
      console.error('FMCSA verification error:', e);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  if (mode === 'pending') {
    return (
      <div>
        <div className="mb-6">
          <h2 className="text-foreground font-bold text-2xl mb-2">Verifying your authority</h2>
          <p className="text-muted-foreground text-sm">
            Thanks, we've got your {authorityType} number. We're finishing verification on our end,
            and you can keep going in the meantime.
          </p>
        </div>
        <Button
          onClick={onVerified}
          className="w-full bg-[hsl(var(--brand-teal))] hover:bg-[hsl(var(--brand-teal))]/90 text-white font-medium py-5 rounded-lg"
        >
          Continue
        </Button>
      </div>
    );
  }

  if (mode === 'manual') {
    return (
      <div>
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
        )}
        <div className="mb-6">
          <h2 className="text-foreground font-bold text-2xl mb-2">Verify your authority</h2>
          <p className="text-muted-foreground text-sm">
            Our verification service is temporarily unavailable. Enter your authority details
            and we'll review within 24 hours.
          </p>
        </div>
        <ManualFmcsaForm
          prefillMc={authorityType === 'MC' ? code : ''}
          prefillDot={authorityType === 'DOT' ? code : ''}
          defaultEntity={role === 'broker' ? 'broker' : 'carrier'}
          onSubmitted={() => onVerified()}
        />
      </div>
    );
  }

  return (
    <div>
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
      )}
      <div className="mb-6">
        <h2 className="text-foreground font-bold text-2xl mb-2">Verify your authority</h2>
        <p className="text-muted-foreground text-sm">
          Confirm your {role === 'broker' ? 'MC' : 'DOT'} number so we can activate your account
          with real FMCSA data. This prevents fake accounts and unlocks your verified badge.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="sfmc_authority_type" className="text-muted-foreground text-xs mb-2 block">
            Authority type
          </Label>
          <Select
            value={authorityType}
            onValueChange={(v: 'MC' | 'DOT') => {
              setAuthorityType(v);
              setError('');
              setVerified(null);
            }}
          >
            <SelectTrigger
              id="sfmc_authority_type"
              className="border-border text-foreground focus:border-[hsl(var(--brand-teal))] rounded-lg py-5"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MC">MC Number</SelectItem>
              <SelectItem value="DOT">DOT Number</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="sfmc_code" className="text-muted-foreground text-xs mb-2 block">
            {authorityType} Number
          </Label>
          <Input
            id="sfmc_code"
            type="text"
            inputMode="numeric"
            value={code}
            onChange={handleCodeChange}
            placeholder={authorityType === 'MC' ? 'e.g., 123456' : 'e.g., 686931'}
            className="border-border text-foreground focus:border-[hsl(var(--brand-teal))] rounded-lg py-4"
          />
          {error && (
            <p className="text-xs text-destructive mt-2 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {error}
            </p>
          )}
        </div>

        {verified && (
          <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="h-4 w-4 text-[hsl(var(--brand-teal))]" />
              <p className="text-foreground font-semibold text-sm">Company verified</p>
            </div>
            <p className="text-muted-foreground text-xs">
              Confirm this is your company before continuing.
            </p>
            <dl className="grid grid-cols-1 gap-y-1.5 text-xs mt-3">
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Legal name</dt>
                <dd className="text-foreground font-medium text-right">{verified.legalName || '—'}</dd>
              </div>
              {verified.dbaName && (
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">DBA</dt>
                  <dd className="text-foreground font-medium text-right">{verified.dbaName}</dd>
                </div>
              )}
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">MC #</dt>
                <dd className="text-foreground font-medium text-right">{verified.mcNumber || '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">DOT #</dt>
                <dd className="text-foreground font-medium text-right">{verified.dotNumber || '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Authority</dt>
                <dd className="text-foreground font-medium text-right">
                  {verified.statusCode === 'A' ? 'Active' : verified.statusCode || '—'}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Address</dt>
                <dd className="text-foreground font-medium text-right">
                  {(() => {
                    const parts = [
                      verified.phyStreet,
                      [verified.phyCity, verified.phyState].filter(Boolean).join(', '),
                      verified.phyZipcode,
                    ].filter(Boolean);
                    return parts.length ? parts.join(' • ') : '—';
                  })()}
                </dd>
              </div>
            </dl>
          </div>
        )}

        <p className="text-muted-foreground text-xs">
          Find your number at{' '}
          <a
            href="https://safer.fmcsa.dot.gov/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[hsl(var(--brand-teal))] hover:underline"
          >
            FMCSA SAFER System ↗
          </a>
        </p>

        {scanning ? (
          <div className="mt-2">
            <FmcsaProgressBar active={scanning} />
          </div>
        ) : verified ? (
          <Button
            onClick={onVerified}
            className="w-full bg-[hsl(var(--brand-teal))] hover:bg-[hsl(var(--brand-teal))]/90 text-white font-medium py-5 rounded-lg mt-2"
          >
            Continue
          </Button>
        ) : (
          <Button
            onClick={handleVerify}
            disabled={verifying || !code.trim() || !!error}
            className="w-full bg-[hsl(var(--brand-teal))] hover:bg-[hsl(var(--brand-teal))]/90 text-white font-medium py-5 rounded-lg mt-2 disabled:opacity-50"
          >
            {verifying ? 'Verifying…' : 'Verify authority'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default SignupFmcsaStep;
